import type { CatalogEntry } from '../types';

/**
 * Profile (feature `06`) error codes + catalog.
 *
 * Code shape is `06AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`0600xx`, AA=00) are recurring cross-endpoint conditions —
 * `UserNotFound` is thrown by `getProfile`, so it collapses to ONE code so the
 * same condition reads the same everywhere.
 *
 * Endpoint → `AA` map:
 *   getProfile=01, updateProfile=02, uploadAvatar=03, updateNotifications=04 (RETIRED
 *   2026-08-03 — endpoint removed with the Preferences page; do NOT reuse 04),
 *   getConnectedLogins=05 (RETIRED 2026-09-09 — connected-logins feature removed;
 *   do NOT reuse 05), unlinkProvider=06 (RETIRED 2026-09-09 — do NOT reuse 06).
 *
 * The `UploadAvatar.InvalidFile` condition wraps `S3AvatarService.validateFile`'s
 * technical reason (unsupported mimetype / size in bytes). That detail is NOT
 * folded into the user message — the catalog prose stays generic and the raw
 * reason is preserved on the `AppException` `cause` for the server log only.
 */
export const PROFILE_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    UserNotFound: '060001',
  },
  /** uploadAvatar=03 (multipart + file validation). */
  UploadAvatar: {
    NotMultipart: '060301',
    NoFile: '060302',
    InvalidFile: '060303',
    UploadFailed: '060304',
  },
} as const;

export const PROFILE_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '060001': { httpStatus: 404, message: "We couldn't load your profile." },

  // ── Upload avatar ──
  '060301': { httpStatus: 400, message: 'Please upload your file again.' },
  '060302': { httpStatus: 400, message: 'Please choose a file to upload.' },
  '060303': {
    httpStatus: 400,
    message: 'That file is not a supported image. Use a JPEG, PNG, WebP or GIF under 5 MB.',
  },
  '060304': {
    httpStatus: 502,
    message: "We couldn't upload your photo. Please try again.",
  },
};
